import { Text, Box, IconButton, Icon, useDisclosure, Tooltip, UnorderedList, ListItem } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CellValue } from 'react-table';

import { IconBan, IconPen, IconResend, IconTrashbin, IconView } from 'assets/icons';
import { ConfirmationModal } from 'components/Modal';
import Table from 'components/Table';
import TrackLocationModal from 'containers/Employees/Modal/TrackLocationModal';
import TrackMileageModal from 'containers/Employees/Modal/TrackMileageModal';
import { getEmployeesCommonColumns } from 'containers/Employees/shared/commonColumns';
import DeleteUserModal from 'containers/Employees/shared/DeleteUserModal';
import FilterGroup from 'containers/Employees/shared/FilterGroup';
import { roles } from 'global/rolesAndPermissions';
import { routePaths } from 'global/routePaths';
import { IDeactivateEmployeeData, useDeactivateEmployees } from 'hooks/employee/useDeactivateEmployees';
import { useDeleteEmployees } from 'hooks/employee/useDeleteEmployees';
import { IEmployeeSearchQuery, useFetchPaginatedEmployee } from 'hooks/employee/useFetchPaginatedEmployee';
import { useResendInvite } from 'hooks/employee/useResendInvite';
import { useTrackLocationForEmployees } from 'hooks/employee/useTrackLocationForEmployees';
import { useTrackMileageForEmployees } from 'hooks/employee/useTrackMileageForEmployees';
import { useEmployeePageStore } from 'store/useEmployeePageStore';
import { useUserInfo } from 'store/useUserStore';

import ActiveTableFooter from './ActiveTableFooter';

const searchQueryInitials: IEmployeeSearchQuery = {
  page: 1,
  page_size: 25,
  active: '1',
  role: 'all',
  groupId: '',
  search: '',
  sort: '',
};

interface IActiveEmployeesProps {
  initialSearchQuery?: IEmployeeSearchQuery;
}

const Active: React.FC<IActiveEmployeesProps> = ({ initialSearchQuery = searchQueryInitials }) => {
  const { t } = useTranslation(['employees', 'common']);
  const userInfo: any = useUserInfo();
  const {
    updateEmployeeTableState,
    employeeTableState,
    employeeHasNotRedirected,
    employeeRedirected,
    clearEmployeeTableState,
  } = useEmployeePageStore(state => state);

  const [searchQuery, setSearchQuery] = useState<IEmployeeSearchQuery>(initialSearchQuery);

  const [dialogIds, setDialogIds] = useState<IDeactivateEmployeeData>({ user_ids: [] });
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteName, setDeleteName] = useState<string | null>(null);

  const navigate = useNavigate();
  const {
    isOpen: isDeactivateModalOpen,
    onOpen: onDeactivateModalOpen,
    onClose: onDeactivateModalClose,
  } = useDisclosure();
  const {
    isOpen: isResendInviteModalOpen,
    onOpen: onResendInviteModalOpen,
    onClose: onResendInviteModalClose,
  } = useDisclosure();
  const {
    isOpen: isTrackMileageModalOpen,
    onOpen: onTrackMileageModalOpen,
    onClose: onTrackMileageModalClose,
  } = useDisclosure();
  const {
    isOpen: isTrackLocationModalOpen,
    onOpen: onTrackLocationModalOpen,
    onClose: onTrackLocationModalClose,
  } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const { status, data, refetch } = useFetchPaginatedEmployee(searchQuery);

  const { status: deactivateEmployeesStatus, mutateAsync: mutateAsyncDeactivatedEmployees } = useDeactivateEmployees();
  const { status: deleteEmployeesStatus, mutateAsync: mutateAsyncDeletedEmployees } = useDeleteEmployees();
  const { status: resendInviteStatus, mutateAsync: mutateAsyncResendInvite } = useResendInvite();
  const { status: trackMileageStatus, mutateAsync: mutateAsyncTrackMileage } = useTrackMileageForEmployees();
  const { status: trackLocationStatus, mutateAsync: mutateAsyncTrackLocation } = useTrackLocationForEmployees();

  // const showAssignedPayroll: boolean =
  //   featureList[FEATURES.multiple_payroll_overtime] &&
  //   (companyInfo?.plan?.toLowerCase() === PLANS.freeTrial || companyInfo?.plan?.toLowerCase() === PLANS.premium);

  const columns = useMemo(
    () => [
      ...getEmployeesCommonColumns(userInfo, t, navigate),
      // {
      //   Header: 'Phone Number',
      //   accessor: 'phone',
      // },
      // {
      //   Header: showAssignedPayroll && t('employees:assignedPayroll'),
      //   accessor: 'assignedPayroll',
      //   width: showAssignedPayroll ? 220 : 0,
      //   Cell: (data: CellValue) => {
      //     const payrollName = data.row.original?.assignedPayroll?.name;
      //     return showAssignedPayroll && payrollName ? (
      //       <Flex>
      //         <Tag
      //           bg="#FFDFA8"
      //           color="gray.700"
      //           borderRadius="sm"
      //           fontWeight="semibold"
      //           onClick={() =>
      //             navigate(
      //               routePaths.companySettings.payrollAndOvertime.view.replace(
      //                 ':id',
      //                 data.row.original?.assignedPayroll?.id
      //               )
      //             )
      //           }
      //           cursor="pointer"
      //         >
      //           {payrollName}
      //         </Tag>
      //       </Flex>
      //     ) : (
      //       ''
      //     );
      //   },
      // },
      {
        Header: '',
        accessor: 'action',
        width: 240,
        Cell: (data: CellValue) => {
          return (
            <Box>
              {userInfo?.roleId === roles.ADMIN ||
              (userInfo?.roleId === roles.MANAGER && data.row.original?.roleId !== roles.ADMIN) ? (
                <Tooltip label={t('employees:viewUser')} hasArrow bg="black">
                  <IconButton
                    data-testid="button-view-user"
                    aria-label="View"
                    bg="rgba(38, 103, 201, 0.1)"
                    icon={<Icon as={IconView} sx={{ '& path': { fill: 'primary.600' } }} color="primary.600" />}
                    size="sm"
                    m="1"
                    _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                    onClick={() => navigate(routePaths.employees.detail.replace(':id', data.row.original?.userId))}
                  />
                </Tooltip>
              ) : null}
              {userInfo?.roleId === roles.ADMIN ? (
                <>
                  <Tooltip label={t('employees:updateUser')} hasArrow bg="black">
                    <IconButton
                      data-testid="button-edit-user"
                      aria-label="Edit"
                      bg="rgba(38, 103, 201, 0.1)"
                      icon={<Icon as={IconPen} />}
                      size="sm"
                      m="1"
                      _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                      onClick={() => navigate(routePaths.employees.edit.replace(':id', data.row.original?.userId))}
                    />
                  </Tooltip>
                  {userInfo?.userId !== data.row.original?.userId ? (
                    <>
                      {!data.row.original.is_register ? (
                        <Tooltip label={t('employees:resendInvitation')} hasArrow bg="black">
                          <IconButton
                            data-testid="button-resend-invitation"
                            aria-label="Resend"
                            bg="rgba(101, 104, 134, 0.1)"
                            icon={<Icon as={IconResend} />}
                            size="sm"
                            m="1"
                            _hover={{ bg: 'rgba(101, 104, 134, 0.2)' }}
                            onClick={() => handleSingleResentInvite(data.row.original?.userId)}
                          />
                        </Tooltip>
                      ) : null}
                      <Tooltip label={t('employees:deactivateUser')} hasArrow bg="black">
                        <IconButton
                          data-testid="button-deactivate-user"
                          aria-label="Deactivate"
                          bg="rgba(101, 104, 134, 0.1)"
                          icon={<Icon as={IconBan} />}
                          size="sm"
                          m="1"
                          _hover={{ bg: 'rgba(101, 104, 134, 0.2)' }}
                          onClick={() => handleSingleDeactivate(data.row.original.userId)}
                        />
                      </Tooltip>
                      <Tooltip label={t('employees:deleteUser')} hasArrow bg="black">
                        <IconButton
                          data-testid="button-delete-user"
                          aria-label="Delete"
                          bg="rgba(255, 83, 83, 0.1)"
                          icon={<Icon as={IconTrashbin} />}
                          size="sm"
                          m="1"
                          _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                          onClick={() =>
                            handleSingleDelete(
                              data.row.original.userId,
                              data.row.original.firstName + ' ' + data.row.original.lastName
                            )
                          }
                        />
                      </Tooltip>
                    </>
                  ) : null}
                </>
              ) : null}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Caching the current page number so that we know which page number the user's table was in before opening a new tab
  useEffect(() => {
    if (!employeeRedirected) {
      if (searchQuery) {
        const newEmployeeTableState: any = {
          ...employeeTableState,
          current_page: searchQuery.page,
        };
        updateEmployeeTableState(newEmployeeTableState);
      }
    }

    employeeHasNotRedirected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    // Resets the values on page reload
    if (!employeeRedirected) {
      window.addEventListener('beforeunload', () => clearEmployeeTableState());
      return () => {
        window.removeEventListener('beforeunload', () => clearEmployeeTableState());
      };
    }

    // Updating the searchQuery to match the redirect data
    setSearchQuery(prevState => ({
      ...prevState,
      page: employeeTableState?.current_page,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter Group handler
  const handleRoleChange = useCallback(
    (selectedRole: ILabelValueOptions) => {
      if (selectedRole) {
        setSearchQuery({ ...searchQuery, page: 1, role: selectedRole.value });
      } else {
        setSearchQuery({ ...searchQuery, page: 1, role: 'all' });
      }
    },
    [searchQuery]
  );

  const handleSortChange = useCallback(
    (selectedSort: ILabelValueOptions) => {
      if (selectedSort) {
        setSearchQuery({ ...searchQuery, page: 1, sort: selectedSort.value });
      } else {
        setSearchQuery({ ...searchQuery, page: 1, sort: '' });
      }
    },
    [searchQuery]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handleSingleResentInvite = (id: number) => {
    setDialogIds({ user_ids: [id] });
    onResendInviteModalOpen();
  };

  const resendInvites = () => {
    mutateAsyncResendInvite({ user_id: dialogIds.user_ids[0] }).then(() => {
      refetch();
      onResendInviteModalClose();
      setDialogIds({ user_ids: [] });
    });
  };

  // deactivate user handler
  const handleSingleDeactivate = (id: number) => {
    setDialogIds({ user_ids: [id] });
    onDeactivateModalOpen();
  };

  const handleBulkDeactivate = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.userId);
    setDialogIds({ user_ids: ids });
    onDeactivateModalOpen();
  };

  const deactivateUsers = () => {
    mutateAsyncDeactivatedEmployees(dialogIds).then(() => {
      refetch();
      onDeactivateModalClose();
      setDialogIds({ user_ids: [] });
    });
  };

  // delete user handler
  const handleSingleDelete = (id: number, name: string) => {
    setDeleteId(id);
    setDeleteName(name);
    onDeleteModalOpen();
  };

  const deleteUser = (
    _e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    setIsChecked: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    mutateAsyncDeletedEmployees(deleteId!).then(() => {
      refetch();
      setIsChecked(false);
      onDeleteModalClose();
      setDialogIds({ user_ids: [] });
    });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize, page: 1 }));
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  // Track mileage
  const handleBulkTrackMileage = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.userId);
    setDialogIds({ user_ids: ids });
    onTrackMileageModalOpen();
  };

  const handleTrackMileageFormSubmit = (values: any) => {
    const postData = { ids: dialogIds.user_ids, track_mileage: values.trackMileage };
    mutateAsyncTrackMileage(postData).then(() => {
      refetch();
      onTrackMileageModalClose();
      setDialogIds({ user_ids: [] });
    });
  };

  // Track mileage
  const handleBulkTrackLocation = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.userId);
    setDialogIds({ user_ids: ids });
    onTrackLocationModalOpen();
  };

  const handleTrackLocationFormSubmit = (values: any) => {
    const postData = { ids: dialogIds.user_ids, track_location: values.trackLocation };
    mutateAsyncTrackLocation(postData).then(() => {
      refetch();
      onTrackLocationModalClose();
      setDialogIds({ user_ids: [] });
    });
  };

  const handleDeactivateFromDelete = () => {
    onDeleteModalClose();
    setDialogIds({ user_ids: [deleteId as number] });
    onDeactivateModalOpen();
  };

  return (
    <div>
      <ConfirmationModal
        isOpen={isResendInviteModalOpen}
        onClose={onResendInviteModalClose}
        title={t('employees:resendInvitationTitle')}
        subTitle={t('employees:resendInvitationSubTitle')}
        confirmButtonLabel={t('employees:resendInvite')}
        confirmButtonProps={{
          bg: 'rgba(72,82, 175, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          isLoading: resendInviteStatus === 'loading',
          onClick: resendInvites,
        }}
      />
      <ConfirmationModal
        isOpen={isDeactivateModalOpen}
        onClose={onDeactivateModalClose}
        title={t('employees:deactivateUserTile')}
        subTitle={t('employees:deactivateUserSubTitle')}
        confirmButtonLabel={t('common:deactivate')}
        confirmButtonProps={{
          bg: 'rgba(72,82, 175, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          isLoading: deactivateEmployeesStatus === 'loading',
          onClick: deactivateUsers,
        }}
      />
      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setDeleteId(null);
          setDeleteName(null);
          onDeleteModalClose();
        }}
        title={`Permanently delete ${deleteName}'s profile?`}
        body={
          <Box>
            <Text mb={3}>{t('employees:deleteUserModal.title')}</Text>
            <UnorderedList pl={4}>
              <ListItem>{t('employees:deleteUserModal.listItem1')}</ListItem>
              <ListItem>{t('employees:deleteUserModal.listItem2')}</ListItem>
            </UnorderedList>
            <Text mt={3}>
              {t('employees:deleteUserModal.footerItem1')}{' '}
              <Text
                as="span"
                textTransform="lowercase"
                color="primary.500"
                cursor="pointer"
                onClick={handleDeactivateFromDelete}
              >
                {t('common:deactivate')}
              </Text>{' '}
              {t('employees:deleteUserModal.footerItem2')}
            </Text>
          </Box>
        }
        confirmButtonLabel={t('employees:deleteUser')}
        confirmButtonProps={{
          // bg: 'rgba(255, 83, 83, 0.1)',
          bg: '#E01E5B',
          color: 'white',
          _hover: {
            bg: 'rgba(224, 30, 91, 0.8)',
          },
          isLoading: deleteEmployeesStatus === 'loading',
          onClick: deleteUser,
        }}
      />

      {/* Track Mileage */}
      <TrackMileageModal
        isOpen={isTrackMileageModalOpen}
        onClose={onTrackMileageModalClose}
        isLoading={trackMileageStatus === 'loading'}
        handleFormSubmit={handleTrackMileageFormSubmit}
      />

      {/* Track Location */}
      <TrackLocationModal
        isOpen={isTrackLocationModalOpen}
        onClose={onTrackLocationModalClose}
        isLoading={trackLocationStatus === 'loading'}
        handleFormSubmit={handleTrackLocationFormSubmit}
      />

      <FilterGroup
        handleRoleChange={handleRoleChange}
        handleSortChange={handleSortChange}
        handleSearchInputChange={handleSearchInputChange}
      />
      <Table
        columns={columns}
        data={data?.payload || []}
        paginationStyle="rowsPerPage"
        paginationData={data?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        isLoading={status === 'loading'}
        enableRowSelect
        emptyTableStateProps={{
          searchText: searchQuery.search,
        }}
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected) => {
          const updatedSelectedFlatRows = selectedFlatRows.filter(
            (row: any) => row.original?.userId !== userInfo?.userId
          );

          return (
            <ActiveTableFooter
              selectedFlatRows={selectedFlatRows}
              updatedSelectedFlatRows={updatedSelectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleBulkTrackMileage={handleBulkTrackMileage}
              handleBulkTrackLocation={handleBulkTrackLocation}
              handleBulkDeactivate={handleBulkDeactivate}
            />
          );
        }}
      />
    </div>
  );
};

export default Active;
